import { Component, Input, OnInit } from '@angular/core';
import { SongService } from '../../services/song.service';
import { ComposerService } from '../../services/composer.service';
import { AuthService } from '../../services/auth.service';
import { Song } from '../../models/song';
import { SongDialogComponent } from './song-dialog.component';
import { Composer } from '../../models/composer';
import { FormBuilder, FormGroup } from '@angular/forms';
import { User } from '../../models/user';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ResponsiveService } from '../../core/responsive.service'; // Import ResponsiveService
import { Observable } from 'rxjs';

@Component({
  selector: 'app-songs',
  templateUrl: './songs.component.html',
  styleUrls: ['./songs.component.scss'], // Updated to .scss
  standalone: false
})
export class SongsComponent implements OnInit {
  @Input() resourceFolder!: string; // Ensure this is a public property

  songs: Song[] = [];
  filteredSongs: Song[] = [];
  composers: Composer[] = []; // Add composers array
  searchQuery: string = '';
  selectedComposerId: number | 'ALL' = 'ALL'; // Default to (All Composers)
  currentUserIsAdmin: boolean = false;
  displayedColumns: string[] = []; // Initialize as empty
  editForm: FormGroup;
  selectedSong: Song | null = null;
  viewMode: boolean = false;
  // Expose handset observable for template usage
  isHandset$!: Observable<boolean>;
  private currentIsHandset = false;

  constructor(
    private songService: SongService,
    private composerService: ComposerService,
    private fb: FormBuilder,
    private authService: AuthService, // Inject AuthService
    private dialog: MatDialog, // Inject MatDialog
    private route: ActivatedRoute, // Inject ActivatedRoute
    private responsiveService: ResponsiveService // Inject ResponsiveService
  ) {
    this.editForm = this.fb.group({
      id: [0],
      name: [''],
      genre: [''],
      genreGroup: [''],
      voicing: [''],
      instruments: [''],
      language: [''],
      year: [null],
      composerId: [null],
      resourceFolder: [''],
      comments: [''] // Add Comments field
    });
  }

  ngOnInit(): void {
    console.log('SongComponent initialized with resourceFolder:', this.resourceFolder);
    this.loadComposers(); // Load composers

    this.currentUserIsAdmin = this.authService.isCurrentUserAdmin();

    // Set default columns for desktop
    this.displayedColumns = this.currentUserIsAdmin
      ? ['name', 'composer', 'language', 'year', 'genre', 'genreGroup', 'voicing', 'instruments', 'actions'] // Admin: All columns
      : ['name', 'composer', 'language', 'year', 'genre', 'genreGroup', 'voicing', 'instruments']; // Non-Admin: Exclude actions

    // Expose observable after service is constructed
    this.isHandset$ = this.responsiveService.isHandset$;

    // Adjust columns based on device type
    this.isHandset$.subscribe(isHandset => {
      this.currentIsHandset = isHandset;
      this.displayedColumns = isHandset
        ? ['name', 'composer'] // Handset: Only Song Name and Composer Name
        : this.currentUserIsAdmin
          ? ['name', 'composer', 'language', 'year', 'genre', 'genreGroup', 'voicing', 'instruments', 'actions'] // Desktop: Full columns for admins
          : ['name', 'composer', 'language', 'year', 'genre', 'genreGroup', 'voicing', 'instruments']; // Desktop: Exclude actions for non-admins
    });

    // Read composerId from query parameters and preselect the dropdown
    this.route.queryParams.subscribe(params => {
      if (params && typeof params['composerId'] !== 'undefined' && params['composerId'] !== null && params['composerId'] !== '') {
        const parsed = Number(params['composerId']);
        this.selectedComposerId = Number.isFinite(parsed) ? parsed : 'ALL';
      } else {
        // Default to (All Composers)
        this.selectedComposerId = 'ALL';
      }
      this.loadSongs(); // Load songs after reading query parameters
    });
  }

  loadSongs(): void {
    this.songService.getSongs().subscribe(data => {
      this.songs = data;
  this.filterByComposer(); // Filter songs after loading them
    });
  }

  loadComposers(): void {
    this.composerService.getComposers().subscribe(data => {
      // Sort composers alphabetically by display name (prefer surname, fallback to fullName)
      this.composers = [...data].sort((a, b) => {
        const an = (a?.surname || a?.fullName || '').toString();
        const bn = (b?.surname || b?.fullName || '').toString();
        if (!an && !bn) return 0;
        if (!an) return 1;
        if (!bn) return -1;
        return an.localeCompare(bn, undefined, { sensitivity: 'base' });
      });
      // If a specific composer is selected but doesn't exist, reset to (All Composers)
      if (this.selectedComposerId !== 'ALL') {
        const exists = this.composers?.some(c => c.id === this.selectedComposerId);
        if (!exists) this.selectedComposerId = 'ALL';
      }
    });
  }

  getComposerName(composerId: number | null): string {
    if (!composerId) return 'No composer';
    const composer = this.composers.find(c => c.id === composerId);
    return composer ? `${composer.fullName}` : 'Unknown composer';
  }

  viewSong(song: Song): void {
    this.dialog.open(SongDialogComponent, {
      data: { song, viewMode: true },
  width: '1000px',
  maxWidth: '90vw'
    });
  }

  editSong(song: Song): void {
    const dialogRef = this.dialog.open(SongDialogComponent, {
  data: { song, viewMode: false },
  width: '1000px',
  maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe(result => {
      // Dialog returns true on successful save; refresh the list
      if (result) {
        this.loadSongs();
      }
    });
  }

  onAddSong(): void {
    // Open the dialog for creating a new song
    this.openDialog(null, false);
  }

  saveChanges(): void {
    if (this.selectedSong && this.selectedSong.id === 0) {
      // Add new song using createSong
      this.songService.createSong(this.editForm.value).subscribe(() => {
        this.selectedSong = null;
        this.viewMode = false;
        this.loadSongs();
      });
    } else if (this.selectedSong) {
      // Update existing song
      this.songService.updateSong(this.selectedSong.id, this.editForm.value)
        .subscribe(() => {
          this.loadSongs();
          this.closeSong();
        });
    }
  }

  closeSong(): void {
    this.selectedSong = null;
    this.viewMode = false;
  }

  deleteSong(songId: number): void {
    if (confirm('Are you sure you want to delete this song?')) {
      this.songService.deleteSong(songId).subscribe(() => {
        this.loadSongs();
      });
    }
  }

  // Add search method
  searchSongs(): void {
    console.log('Search query:', this.searchQuery); // Debug log
    const query = this.searchQuery?.toLowerCase().trim() || '';
    
    this.filteredSongs = this.songs.filter(song => {
      // Search in song name
      const songName = song.name?.toLowerCase() || '';
      
      // Get composer name for searching
      let composerName = '';
      if (song.composerId) {
        const composer = this.composers.find(c => c.id === song.composerId);
        if (composer) {
          composerName = `${composer.fullName || ''} ${composer.surname || ''}`.toLowerCase();
        }
      }
      
      return songName.includes(query) || composerName.includes(query);
    });
    
  // Default sort by Song Name (first column)
  this.sortFilteredSongsByName();
  console.log('Filtered songs:', this.filteredSongs); // Debug log
  }

  openDialog(song: Song | null, viewMode: boolean): void {
    const dialogRef = this.dialog.open(SongDialogComponent, {
  width: '1000px',
  maxWidth: '90vw',
      data: { song, viewMode }
    });

    dialogRef.afterClosed().subscribe(result => {
  // Dialog returns true on successful save; refresh the list
  if (result) this.loadSongs();
    });
  }

  filterByComposer(): void {
    if (this.selectedComposerId === 'ALL') {
      this.filteredSongs = this.songs; // Show all songs if no composer is selected
    } else {
      const selectedId = Number(this.selectedComposerId);
      this.filteredSongs = this.songs.filter(song => song.composerId === selectedId);
    }
    // Default sort by Song Name (first column)
    this.sortFilteredSongsByName();
  }

  /** Sort helper: sorts filteredSongs by name ascending, case-insensitive, nulls last */
  private sortFilteredSongsByName(): void {
    this.filteredSongs = [...(this.filteredSongs || [])].sort((a, b) => {
      const an = (a?.name || '').toString();
      const bn = (b?.name || '').toString();
      if (!an && !bn) return 0;
      if (!an) return 1; // place empty/null names last
      if (!bn) return -1;
      return an.localeCompare(bn, undefined, { sensitivity: 'base' });
    });
  }
}